import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import logger from "../../utils/logger";
import { Calendar, AlertTriangle, FileText, Clock } from "lucide-react";

const API = `${window.location.origin}/api`;

const SEVERITY_COLORS = {
  critical: { bg: "rgba(220,38,38,0.15)", color: "#dc2626", border: "#dc262633" },
  high: { bg: "rgba(239,68,68,0.1)", color: "#ef4444", border: "#ef444433" },
  medium: { bg: "rgba(245,158,11,0.08)", color: "#f59e0b", border: "#f59e0b33" },
};

const TYPE_LABELS = { fiu: "FIU-IND", rbi: "RBI" };

export function RegulatoryCalendar() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dashboard/extended-stats`, { withCredentials: true });
      setEvents(data.regulatory_calendar || []);
      setStats(data);
    } catch (err) {
      logger.error("Failed to fetch extended stats:", err);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const now = new Date();

  return (
    <div data-testid="regulatory-calendar">
      {/* Extended Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[
          { label: "STRs Filed", value: stats.strs_filed_month || 0, icon: FileText, color: "#FF6B35", to: "/str-generator" },
          { label: "Bulk Screenings", value: stats.bulk_screenings_run || 0, icon: FileText, color: "#2563eb", to: "/screening" },
          { label: "Txn Alerts Today", value: stats.txn_alerts_today || 0, icon: AlertTriangle, color: "#ef4444", to: "/transactions" },
          { label: "Data Sources", value: `${stats.data_sources_active || 0}/${stats.data_sources_total || 10}`, icon: Clock, color: "#10b981", to: "/settings?tab=data_sources" },
        ].map(card => {
          const CIcon = card.icon;
          const onClick = card.to ? () => navigate(card.to) : undefined;
          return (
            <div
              key={card.label}
              data-testid={`ext-stat-${card.label.toLowerCase().replace(/\s/g, "-")}`}
              className="card-aml"
              onClick={onClick}
              style={{
                padding: "14px",
                cursor: card.to ? "pointer" : "default",
                transition: "border-color 0.15s ease, transform 0.15s ease",
              }}
              onMouseEnter={(e) => { if (card.to) { e.currentTarget.style.borderColor = card.color; e.currentTarget.style.transform = "translateY(-1px)"; } }}
              onMouseLeave={(e) => { if (card.to) { e.currentTarget.style.borderColor = ""; e.currentTarget.style.transform = ""; } }}
            >
              <div className="flex items-center gap-2 mb-2">
                <div style={{ width: "28px", height: "28px", borderRadius: "7px", background: `${card.color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CIcon style={{ width: "14px", height: "14px", color: card.color }} />
                </div>
              </div>
              <div style={{ fontSize: "22px", fontWeight: 700, color: "#f1f5f9", lineHeight: 1 }}>{card.value}</div>
              <div style={{ fontSize: "10px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "1px", color: "#475569", marginTop: "2px" }}>{card.label}</div>
            </div>
          );
        })}
      </div>

      {/* Calendar Widget */}
      <div className="card-aml">
        <div className="flex items-center gap-2 mb-4">
          <Calendar style={{ width: "16px", height: "16px", color: "#FFD700" }} />
          <h2 style={{ fontSize: "13px", fontWeight: 700, color: "#f1f5f9" }}>Regulatory Calendar</h2>
        </div>
        <div className="space-y-2">
          {events.map((evt, idx) => {
            const sc = SEVERITY_COLORS[evt.severity] || SEVERITY_COLORS.medium;
            const evtDate = new Date(evt.date);
            const daysUntil = Math.ceil((evtDate - now) / 86400000);
            const isPast = daysUntil < 0;
            const isUrgent = daysUntil >= 0 && daysUntil <= 7;

            return (
              <div key={evt.date + evt.title} data-testid={`cal-event-${idx}`} style={{
                display: "flex", alignItems: "center", gap: "12px",
                padding: "10px 12px", background: "#080c12", borderRadius: "8px",
                border: `1px solid ${isUrgent ? sc.border : "#1e2530"}`,
                opacity: isPast ? 0.5 : 1,
              }}>
                <div style={{
                  width: "40px", height: "40px", borderRadius: "8px", flexShrink: 0,
                  background: sc.bg, display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                }}>
                  <span style={{ fontSize: "14px", fontWeight: 700, color: sc.color, lineHeight: 1 }}>
                    {evtDate.getDate()}
                  </span>
                  <span style={{ fontSize: "8px", fontWeight: 600, color: sc.color, textTransform: "uppercase" }}>
                    {evtDate.toLocaleString("en-IN", { month: "short" })}
                  </span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "12px", fontWeight: 600, color: "#f1f5f9" }}>{evt.title}</div>
                  <div style={{ fontSize: "10px", color: "#475569" }}>{evt.description}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span style={{
                    fontSize: "8px", fontWeight: 700, padding: "2px 6px", borderRadius: "3px",
                    background: sc.bg, color: sc.color, textTransform: "uppercase",
                  }}>
                    {TYPE_LABELS[evt.type] || evt.type}
                  </span>
                  {!isPast && (
                    <span style={{
                      fontSize: "10px", fontWeight: 600,
                      color: isUrgent ? "#ef4444" : "#475569",
                    }}>
                      {daysUntil === 0 ? "Today" : `${daysUntil}d`}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
